/*
 * WHAT AN EMAIL SUMMARY HOLDS AND HOW OFTEN (stage 4b phase E, decisions 613,
 * 622, 623). Shared by Settings and onboarding's summaries step, on decision
 * 498's rule that a MECHANISM is shared and the screen around it is not: the
 * three categories and the weekday arithmetic would drift if each screen kept
 * its own copy, and the one that drifted would be onboarding's, which nobody
 * looks at twice. What differs is around this component: Settings saves on
 * every change, onboarding holds the choice until its commit.
 *
 * ONE RADIO GROUP FOR THE CADENCE, the theme control's segmented look and its
 * colour rule: the unselected segments are red because they are the tappable
 * ones. Onboarding offers no Off segment and nothing is preselected (Kevin):
 * declining is that step's "Not now", and a preselected cadence would be a
 * choice the reader did not make.
 *
 * THE WEEKDAY APPEARS ONLY FOR WEEKLY, because a control with no outcome is what
 * decision 418 rules out. Monday to Sunday, the order a week reads in, while the
 * wire keeps Go's Sunday = 0. Day names come from Intl in the reader's language,
 * so they need no strings of their own.
 *
 * THE CATEGORIES RUN IN THE EMAIL'S OWN ORDER -- results, registrations,
 * deadlines and schedule changes (decision 619) -- so what the reader ticks is
 * laid out the way it will arrive. Each carries a line saying what it holds,
 * and the deadlines line says the one thing that would otherwise surprise:
 * a tournament every one of your fencers has already entered sends no
 * deadline (Kevin: "make sure this is clear in the description text").
 */

import { useTranslation } from "react-i18next";

import type { EmailSummary } from "../lib/api";
import styles from "./SummaryChoice.module.css";

/** A cadence that may not have been chosen yet, which onboarding starts from. */
export type SummaryDraft = Omit<EmailSummary, "frequency"> & {
  frequency: EmailSummary["frequency"] | null;
};

export const emptySummaryDraft: SummaryDraft = {
  frequency: null,
  weekday: 2, // Tuesday (decision 613)
  registrations: false,
  deadlines: false,
  results: false,
};

/** Whether a draft says "on" with nothing to include, which nothing saves. */
export function needsCategory(d: SummaryDraft): boolean {
  return (d.frequency === "daily" || d.frequency === "weekly") && !d.registrations && !d.deadlines && !d.results;
}

/** Monday first; Go's numbering on the wire. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];

const CATEGORIES = ["results", "registrations", "deadlines"] as const;

export interface SummaryChoiceProps {
  value: SummaryDraft;
  onChange: (next: SummaryDraft) => void;
  /** Settings offers Off; onboarding's Off is its "Not now" button. */
  withOff: boolean;
  /** Keeps ids unique if two ever render on one page. */
  idPrefix: string;
  disabled?: boolean;
}

export function SummaryChoice({ value, onChange, withOff, idPrefix, disabled = false }: SummaryChoiceProps) {
  const { t, i18n } = useTranslation();
  const dayName = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "en", { weekday: "long", timeZone: "UTC" });
  // 2023-01-01 was a Sunday, so day n of that week is weekday n.
  const nameOf = (day: number) => dayName.format(new Date(Date.UTC(2023, 0, 1 + day)));

  const cadences = withOff ? (["off", "daily", "weekly"] as const) : (["daily", "weekly"] as const);
  const on = value.frequency === "daily" || value.frequency === "weekly";

  const weekly = value.frequency === "weekly";

  return (
    <div className={styles.choice}>
      {/* The weekday hangs beneath the switch it qualifies, so the two rows
          read as one control: no rule between them (Kevin's review). */}
      <div className={weekly ? styles.rowJoined : styles.row}>
        <span className={styles.rowLabel} id={`${idPrefix}-frequency`}>
          {t("emailSummary.frequencyLabel")}
        </span>
        <div className={styles.segmented} role="radiogroup" aria-labelledby={`${idPrefix}-frequency`}>
          {cadences.map((c) => {
            const selected = value.frequency === c;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                className={selected ? styles.segSelected : styles.segUnselected}
                onClick={() => onChange({ ...value, frequency: c })}
              >
                {t(`emailSummary.frequency.${c}`)}
              </button>
            );
          })}
        </div>
      </div>

      {weekly ? (
        <div className={styles.weekdayRow}>
          <label className={styles.rowLabel} htmlFor={`${idPrefix}-weekday`}>
            {t("emailSummary.weekdayLabel")}
          </label>
          <select
            id={`${idPrefix}-weekday`}
            className={styles.select}
            value={value.weekday}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, weekday: Number(e.target.value) })}
          >
            {WEEK.map((d) => (
              <option key={d} value={d}>
                {nameOf(d)}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* OFF COLLAPSES EVERYTHING BELOW THE SWITCH (Kevin's review): with
          nothing being sent there is nothing to configure. The stored choices
          survive the collapse, so switching back on restores them. Onboarding
          starts with no cadence at all and shows the boxes, so the reader can
          choose both in either order. */}
      {value.frequency === "off" ? null : (
      <fieldset className={styles.categories} disabled={disabled}>
        <legend className={styles.legend}>{t("emailSummary.includeLabel")}</legend>
        {CATEGORIES.map((c) => (
          <label key={c} className={styles.category} htmlFor={`${idPrefix}-${c}`}>
            <input
              id={`${idPrefix}-${c}`}
              className={styles.checkbox}
              type="checkbox"
              checked={value[c]}
              onChange={(e) => onChange({ ...value, [c]: e.target.checked })}
            />
            <span className={styles.categoryText}>
              <span className={styles.categoryName}>{t(`emailSummary.category.${c}`)}</span>
              <span className={styles.categoryDetail}>{t(`emailSummary.categoryDetail.${c}`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      )}

      {on && needsCategory(value) ? (
        // Not role="alert": the reader can see the boxes they have not ticked,
        // and it is their next move rather than news from elsewhere.
        <p className={styles.hint}>{t("emailSummary.needsCategory")}</p>
      ) : null}
    </div>
  );
}
